import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { useAuth } from '../../contexts/AuthContext'
import { useKiosk } from '../../contexts/KioskContext'
import { useFamily } from '../../contexts/FamilyContext'
import ThemeToggle from '../ui/ThemeToggle'
import Avatar from '../ui/Avatar'
import Badge from '../ui/Badge'

const { 
  FiHome, FiCheckSquare, FiGift, FiCalendar, FiSettings, 
  FiUser, FiLogOut, FiMenu, FiX, FiTablet, FiBarChart3,
  FiStar, FiUsers, FiActivity
} = FiIcons

const Navbar = () => {
  const { user, logout, family } = useAuth()
  const { isKioskMode } = useKiosk()
  const { getChildPoints } = useFamily()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)

  // Don't show navbar in kiosk mode
  if (isKioskMode) return null

  const primaryNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: FiHome },
    { name: 'Tasks', href: '/tasks', icon: FiCheckSquare },
    { name: 'Rewards', href: '/rewards', icon: FiGift },
    { name: 'Kiosk', href: '/kiosk', icon: FiTablet },
    { name: 'Calendar', href: '/calendar', icon: FiCalendar }
  ]

  const secondaryNavigation = [
    { name: 'Settings', href: '/settings', icon: FiSettings },
    { name: 'Tracking', href: '/reward-tracking', icon: FiBarChart3 }
  ]

  const allNavigation = [...primaryNavigation, ...secondaryNavigation]

  const handleNavigation = (href, source = 'unknown') => {
    console.log(`Navigating to: ${href} from: ${source}`)
    navigate(href)
    setIsMenuOpen(false)
    setShowUserMenu(false)
  }

  const handleUserMenuNavigation = (href, source = 'user-menu') => {
    console.log(`User menu navigating to: ${href} from: ${source}`)
    setShowUserMenu(false)
    navigate(href)
  }

  const handleLogout = async () => {
    try {
      setShowUserMenu(false)
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  // Calculate total family points
  const totalFamilyPoints = family?.children?.reduce((total, child) => {
    return total + getChildPoints(child.id)
  }, 0) || 0

  return (
    <div className="relative">
      <nav className="bg-white dark:bg-gray-900 shadow-lg border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0">
              <button 
                onClick={() => handleNavigation('/dashboard', 'logo')}
                className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <div className="text-2xl">🏠</div>
                <span className="hidden sm:block text-xl font-bold text-primary-600 dark:text-primary-400">
                  Chore Haven
                </span>
              </button>
            </div>

            {/* Desktop Navigation - Primary Items Only */}
            <div className="hidden lg:flex items-center space-x-6">
              {primaryNavigation.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href, 'desktop-nav')}
                  className="flex items-center space-x-1.5 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  <SafeIcon icon={item.icon} className="text-lg" />
                  <span className="font-medium text-sm">{item.name}</span>
                </button>
              ))}
            </div>

            {/* Right Side - Compact Layout */}
            <div className="flex items-center space-x-2">
              {/* Compact Family Points Display - Only on larger screens */}
              {family?.children && family.children.length > 0 && (
                <div className="hidden xl:flex items-center space-x-2">
                  <div className="flex items-center space-x-1.5 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 px-2.5 py-1 rounded-full border border-yellow-200 dark:border-yellow-800">
                    <SafeIcon icon={FiStar} className="text-yellow-500 dark:text-yellow-400 text-sm" />
                    <span className="font-bold text-yellow-700 dark:text-yellow-300 text-sm">
                      {totalFamilyPoints.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 px-2.5 py-1 rounded-full border border-blue-200 dark:border-blue-800">
                    <SafeIcon icon={FiUsers} className="text-blue-500 dark:text-blue-400 text-sm" />
                    <span className="font-bold text-blue-700 dark:text-blue-300 text-sm">
                      {family.children.length}
                    </span>
                  </div>
                </div>
              )}

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    console.log('User menu button clicked, current state:', showUserMenu)
                    setShowUserMenu(!showUserMenu)
                  }}
                  className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 relative z-[60]"
                >
                  <Avatar
                    src={user?.user_metadata?.avatar_url}
                    alt={user?.user_metadata?.full_name || user?.email}
                    size="sm"
                  />
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-24">
                      {user?.user_metadata?.full_name || 'User'}
                    </p>
                  </div>
                </button>

                {/* User Dropdown - Portal-style positioning */}
                <AnimatePresence>
                  {showUserMenu && (
                    <>
                      {/* Backdrop specifically for user menu */}
                      <div
                        className="fixed inset-0 z-[98]"
                        onClick={() => {
                          console.log('User menu backdrop clicked')
                          setShowUserMenu(false)
                        }}
                      />
                      
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-[99]"
                        style={{ position: 'absolute' }}
                      >
                        {/* Secondary Navigation in User Menu for Desktop */}
                        <div className="hidden lg:block">
                          {secondaryNavigation.map((item) => (
                            <button
                              key={item.name}
                              onClick={(e) => {
                                e.stopPropagation()
                                console.log(`User menu item clicked: ${item.name} -> ${item.href}`)
                                handleUserMenuNavigation(item.href, `user-menu-${item.name.toLowerCase()}`)
                              }}
                              className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-left transition-colors duration-150"
                            >
                              <SafeIcon icon={item.icon} />
                              <span>{item.name}</span>
                            </button>
                          ))}
                          <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            console.log('Profile clicked -> /child/profile')
                            handleUserMenuNavigation('/child/profile', 'user-menu-profile')
                          }}
                          className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-left transition-colors duration-150"
                        >
                          <SafeIcon icon={FiUser} />
                          <span>Profile</span>
                        </button>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            console.log('Sign Out clicked')
                            handleLogout()
                          }}
                          className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-left transition-colors duration-150"
                        >
                          <SafeIcon icon={FiLogOut} />
                          <span>Sign Out</span>
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsMenuOpen(!isMenuOpen)
                }}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 relative z-50"
              >
                <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-xl" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation - Outside of navbar for proper z-index - KEEPING UNCHANGED */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-lg absolute top-full left-0 right-0 z-[100]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="space-y-2">
                {allNavigation.map((item) => (
                  <button
                    key={item.name}
                    onClick={(e) => {
                      e.stopPropagation()
                      console.log(`Mobile nav clicked: ${item.name} -> ${item.href}`)
                      handleNavigation(item.href, 'mobile-menu')
                    }}
                    className="flex items-center space-x-3 w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200 text-left"
                  >
                    <SafeIcon icon={item.icon} className="text-lg" />
                    <span className="font-medium">{item.name}</span>
                  </button>
                ))}
              </div>

              {/* Mobile Family Points */}
              {family?.children && family.children.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="px-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Family Points</span>
                      <Badge variant="warning">
                        <SafeIcon icon={FiStar} className="text-xs" />
                        {totalFamilyPoints.toLocaleString()}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Children</span>
                      <Badge variant="primary">
                        <SafeIcon icon={FiUsers} className="text-xs" />
                        {family.children.length}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside overlay - Only for mobile menu */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-[90]"
          onClick={() => {
            setIsMenuOpen(false)
          }}
        />
      )}
    </div>
  )
}

export default Navbar